import { useLangContext } from '../LangProviderContext';
import { ILangOptions, ILangParams, ILangPath } from '../langProvider.t';
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import BinIcon from '../../../components/icon/IconWrapped';
import useForceRerender from '../../../hooks/useForceRerender';
import ImageFileByApi from '../../../components/ImageFileByApi/ImageFileByApi';

interface ILangWithComponentsProps {
    className?: string;
    langPath: ILangPath;
    langParams?: ILangParams;
    langOptions?: ILangOptions;
}

export interface ILangWithComponentsConfig {
    langPrefix: string;
    renderComponent: (matchedAttributesList: string[]) => ReactNode;
}

const LANG_WITH_COMPONENTS_CONFIGS: ILangWithComponentsConfig[] = [
    {
        langPrefix: 'icon',
        renderComponent: (matchedAttributesList: string[]) =>
            <BinIcon name={matchedAttributesList[1]} style={{ fill: matchedAttributesList[3] || undefined }}/>,
    },
    {
        langPrefix: 'img-file-by-api',
        renderComponent: (matchedAttributesList: string[]) =>
            <ImageFileByApi imgFileName={matchedAttributesList[1]}/>,
    },
];

function LangWithComponents(props: ILangWithComponentsProps) {
    const { className, langPath, langParams, langOptions } = props;
    const { getLangPath } = useLangContext();
    const langStringDivRef = useRef(null);
    const forceRerender = useForceRerender();

    // Call rerender, because on first render langStringDivRef === null
    useEffect(() => {
        forceRerender();
    }, [forceRerender]);

    const langString = useMemo(() => {
        let langStr = getLangPath(langPath, langParams, langOptions);
        LANG_WITH_COMPONENTS_CONFIGS
            .map((config) => config.langPrefix)
            .forEach(langPrefix => {
                langStr = langStr.replaceAll(new RegExp(`{{${langPrefix}::([^:}]+)(::([^:}]+))?}}`, 'ig'), (...args: string[]) => {
                    return `<span class="${args[0]}"></span>`;
                });
            });
        return langStr;
    }, [getLangPath, langOptions, langParams, langPath]);

    const renderEntityComponents = useCallback((langPrefix: string, renderChildren: (args: string[]) => ReactNode) => {
        if (!langStringDivRef.current) {
            return null;
        }
        const placeholdersElements: HTMLSpanElement[] = langStringDivRef.current.querySelectorAll(`span[class^="{{${langPrefix}::`);
        return Array.from(placeholdersElements).map((placeholderElement, index) => {
            const className = placeholderElement.className;
            const args = className.match(new RegExp(`{{${langPrefix}::([^:}]+)(::([^:}]+))?}}`, 'i'));
            return <Fragment key={`${className}-${index}`}>
                {
                    createPortal(
                        renderChildren(args),
                        placeholderElement,
                    )
                }
            </Fragment>;
        });
    }, []);

    const renderAllEntitiesComponents = useCallback(() =>
        LANG_WITH_COMPONENTS_CONFIGS.map((config) => {
            return renderEntityComponents(config.langPrefix, config.renderComponent);
        }), [renderEntityComponents]);

    return <>
        <div className={className} ref={langStringDivRef} dangerouslySetInnerHTML={{ __html: langString }}/>
        {renderAllEntitiesComponents()}
    </>;
}

export default LangWithComponents;
